import { Link } from "@/components/ui/link";
import * as React from "react";

import { PageHeader } from "@/components/layout-bits";
import {
  Button,
  Card,
  Input,
  Skeleton,
  StatusBadge,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { useTenants } from "@/lib/query/hooks";
import { formatDate } from "@/lib/utils";

export default function TenantsPage() {
  const { data, isLoading, isError, error, refetch } = useTenants();
  const [q, setQ] = React.useState("");

  const rows = (data ?? []).filter((t) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    return (
      t.slug.toLowerCase().includes(needle) ||
      t.display_name.toLowerCase().includes(needle) ||
      t.status.toLowerCase().includes(needle)
    );
  });

  return (
    <div>
      <PageHeader
        title="Tenants"
        description="Every client, isolated in its own database."
        actions={
          <Button asChild>
            <Link href="/tenants/new">Onboard tenant</Link>
          </Button>
        }
      />

      {isError ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <Card className="p-4">
          <div className="mb-3 max-w-xs">
            <Input
              placeholder="Search tenants…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search tenants"
            />
          </div>
          {isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : rows.length === 0 ? (
            <EmptyState
              title={q ? "No tenants match your search" : "No tenants yet"}
              hint={q ? undefined : "Onboard your first client to get started."}
              action={
                !q ? (
                  <Button asChild>
                    <Link href="/tenants/new">Onboard tenant</Link>
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Name</TH>
                  <TH>Slug</TH>
                  <TH>Status</TH>
                  <TH>Region</TH>
                  <TH>Provisioned</TH>
                  <TH className="text-right">Database</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((t) => (
                  <TR key={t.tenant_id}>
                    <TD>
                      <Link href={`/tenants/${t.slug}`} className="font-medium hover:underline">
                        {t.display_name}
                      </Link>
                    </TD>
                    <TD className="font-mono text-xs text-muted-foreground">{t.slug}</TD>
                    <TD>
                      <StatusBadge status={t.status} />
                    </TD>
                    <TD className="text-sm text-muted-foreground">{t.region ?? "—"}</TD>
                    <TD className="text-sm text-muted-foreground">
                      {formatDate(t.provisioned_at)}
                    </TD>
                    <TD className="text-right font-mono text-xs text-muted-foreground">
                      {t.db_name}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Card>
      )}
    </div>
  );
}
